export function normalizeCarouselIndex(index, count) {
  if (count <= 0) return 0;

  return ((index % count) + count) % count;
}

export function canAutoplayCarousel({ enabled, count, reducedMotion }) {
  return enabled && count > 1 && !reducedMotion;
}

export function getCountdownParts(endTime, now = Date.now()) {
  const remainingSeconds = Math.max(0, Math.floor((endTime - now) / 1000));

  return {
    days: Math.floor(remainingSeconds / 86400),
    hours: Math.floor((remainingSeconds % 86400) / 3600),
    minutes: Math.floor((remainingSeconds % 3600) / 60),
    seconds: remainingSeconds % 60,
    expired: remainingSeconds === 0,
  };
}

export function parseCountdownTime(value, timeZone = 'Asia/Shanghai') {
  if (!value) return Number.NaN;
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return new Date(value).getTime();

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return Number.NaN;

  const [, year, month, day, hour, minute, second = '0'] = match;
  const wallClockTime = Date.UTC(year, Number(month) - 1, day, hour, minute, second);

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const getOffset = (timestamp) => {
      const parts = Object.fromEntries(
        formatter.formatToParts(timestamp).filter(({ type }) => type !== 'literal').map(({ type, value: partValue }) => [type, partValue]),
      );
      const displayedAsUtc = Date.UTC(parts.year, Number(parts.month) - 1, parts.day, parts.hour, parts.minute, parts.second);
      return displayedAsUtc - timestamp;
    };

    let timestamp = wallClockTime - getOffset(wallClockTime);
    timestamp = wallClockTime - getOffset(timestamp);
    return timestamp;
  } catch {
    return Number.NaN;
  }
}

class NovaHomeCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-nova-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-nova-hero-dot]')];
    this.previousButton = this.querySelector('[data-nova-hero-previous]');
    this.nextButton = this.querySelector('[data-nova-hero-next]');
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.pageLoaded = document.readyState === 'complete';
    this.countdownTimeZone = this.dataset.countdownTimeZone || 'Asia/Shanghai';
    this.countdowns = [...this.querySelectorAll('[data-nova-countdown]')]
      .map((element) => ({
        element,
        endTime: parseCountdownTime(element.dataset.countdownEnd, this.countdownTimeZone),
      }))
      .filter(({ endTime }) => Number.isFinite(endTime));

    this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
    this.previousButton?.addEventListener('click', this.handlePreviousClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);
    this.addEventListener('mouseenter', this.stop);
    this.addEventListener('mouseleave', this.start);
    this.addEventListener('focusin', this.stop);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.reducedMotionQuery.addEventListener?.('change', this.handleMotionChange);
    window.addEventListener('load', this.handleWindowLoad, { once: true });

    this.select(this.currentIndex, false);
    this.updateCountdowns();
    if (this.countdowns.length) this.countdownTimer = window.setInterval(this.updateCountdowns, 1000);
    if (this.pageLoaded) this.upgradeSlideImage(this.slides[this.currentIndex]);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    window.clearInterval(this.countdownTimer);
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
    this.previousButton?.removeEventListener('click', this.handlePreviousClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
    this.removeEventListener('mouseenter', this.stop);
    this.removeEventListener('mouseleave', this.start);
    this.removeEventListener('focusin', this.stop);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleMotionChange);
    window.removeEventListener('load', this.handleWindowLoad);
  }

  select(index, restart = true) {
    this.currentIndex = normalizeCarouselIndex(index, this.slides.length);

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('hidden', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', String(dotIndex === this.currentIndex));
    });
    if (this.timer) this.#restartProgress();

    if (restart) {
      this.stop();
      this.start();
    }

    if (this.pageLoaded) this.upgradeSlideImage(this.slides[this.currentIndex]);
  }

  start = () => {
    if (this.timer || document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;

    const enabled = this.dataset.autoplay === 'true';
    const reducedMotion = this.reducedMotionQuery?.matches ?? false;

    if (!canAutoplayCarousel({ enabled, count: this.slides.length, reducedMotion })) return;

    const configuredInterval = Number.parseInt(this.dataset.interval || '5000', 10);
    const interval = Number.isFinite(configuredInterval) ? configuredInterval : 5000;

    this.style.setProperty('--nova-hero-slide-duration', `${interval}ms`);
    this.timer = window.setInterval(() => this.select(this.currentIndex + 1, false), interval);
    this.#restartProgress();
  };

  stop = () => {
    window.clearInterval(this.timer);
    this.timer = undefined;
    this.dots?.forEach((dot) => dot.classList.remove('is-progressing'));
  };

  #restartProgress() {
    const activeDot = this.dots?.[this.currentIndex];
    if (!activeDot) return;

    this.dots.forEach((dot) => dot.classList.remove('is-progressing'));
    // Force the animation to restart when the active slide changes or autoplay resumes.
    void activeDot.offsetWidth;
    activeDot.classList.add('is-progressing');
  }

  handleDotClick = (event) => {
    this.select(Number.parseInt(event.currentTarget.dataset.slideIndex, 10));
  };

  handlePreviousClick = () => {
    this.select(this.currentIndex - 1);
  };

  handleNextClick = () => {
    this.select(this.currentIndex + 1);
  };

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.start();
  };

  handleMotionChange = () => {
    this.stop();
    this.start();
  };

  handleWindowLoad = () => {
    this.pageLoaded = true;
    this.upgradeSlideImage(this.slides[this.currentIndex]);
  };

  upgradeSlideImage(slide) {
    if (!this.desktopQuery.matches || !slide) return;

    const image = slide.querySelector('img[data-nova-hero-high-src]');
    const highSrc = image?.dataset.novaHeroHighSrc;
    if (!highSrc || image.dataset.novaHeroUpgradeState) return;

    image.dataset.novaHeroUpgradeState = 'loading';
    const loadHighQualityImage = () => {
      const highQualityImage = new Image();
      highQualityImage.decoding = 'async';
      highQualityImage.onload = () => {
        image.src = highSrc;
        image.classList.add('is-high-quality');
        delete image.dataset.novaHeroHighSrc;
      };
      highQualityImage.onerror = () => {
        image.classList.add('is-high-quality');
      };
      highQualityImage.src = highSrc;
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadHighQualityImage, { timeout: 1500 });
    } else {
      window.setTimeout(loadHighQualityImage, 0);
    }
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };

  handleBlockSelect = (event) => {
    const slide = event.target.closest?.('[data-nova-hero-slide]');

    if (slide && this.contains(slide)) {
      this.select(Number.parseInt(slide.dataset.slideIndex, 10));
    }
  };

  updateCountdowns = () => {
    this.countdowns?.forEach(({ element, endTime }) => {
      const parts = getCountdownParts(endTime);

      for (const [unit, value] of Object.entries(parts)) {
        if (unit === 'expired') continue;
        const output = element.querySelector(`[data-countdown-unit="${unit.slice(0, -1)}"]`);
        if (output) output.textContent = String(value).padStart(2, '0');
      }

      const accessibleOutput = element.querySelector('[data-countdown-accessible]');
      if (accessibleOutput) {
        const labels = ['Day', 'Hour', 'Minute', 'Second'].map(
          (fallback) => element.dataset[`countdown${fallback}Label`] || fallback,
        );
        accessibleOutput.textContent = parts.expired
          ? (element.dataset.countdownExpiredLabel || 'Offer ended')
          : `${parts.days} ${labels[0]}, ${parts.hours} ${labels[1]}, ${parts.minutes} ${labels[2]}, ${parts.seconds} ${labels[3]}`;
      }
      element.classList.toggle('is-expired', parts.expired);
    });
  };
}

if (!customElements.get('nova-home-carousel')) {
  customElements.define('nova-home-carousel', NovaHomeCarousel);
}
